/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import {
  type AdresseFormValueOptions,
  fromAdresseFormValue,
  toAdresseFormValue,
} from '@/utils';

/**
 * État d'un `AdresseForm` des modales de la fiche, et adresse d'acte qui en
 * découle (cf. `fromAdresseFormValue`).
 *
 * `AdresseForm` calcule l'adresse émise par `onItemChange` dans la fonction de
 * mise à jour de son état, que React n'exécute pas toujours sur-le-champ : il
 * émet alors une adresse vide (ex. saisie du numéro en type « Etranger »).
 * L'appliquer réinitialisait le formulaire, type compris. Comme dans
 * l'assistant, elle est ignorée et chaque champ est repris de `onChange`, qui
 * suit toujours.
 */
const useAdresseFormFiche = (
  adresse: any,
  onAdresseChange: (adresse: Record<string, unknown>) => void,
  options: AdresseFormValueOptions = {},
) => {
  const { articleField } = options;

  const [adresseFormValue, setAdresseFormValue] = React.useState(() =>
    toAdresseFormValue(adresse, { articleField }),
  );
  // `onItemChange` puis `onChange` sont émis dans le même évènement : le
  // second doit partir de l'état laissé par le premier, pas du dernier rendu.
  const courant = React.useRef(adresseFormValue);

  const appliquer = React.useCallback(
    (suivant: any) => {
      // `previous` : permet d'invalider les identifiants du référentiel
      // devenus faux quand un libellé est retapé à la main.
      const previous = courant.current;
      courant.current = suivant;
      setAdresseFormValue(suivant);
      onAdresseChange(
        fromAdresseFormValue(suivant, { previous, articleField }),
      );
    },
    [onAdresseChange, articleField],
  );

  const onItemChange = React.useCallback(
    ({ adresse: suivant }: { adresse: any }) => {
      if (_.isEmpty(suivant)) return;

      appliquer(suivant);
    },
    [appliquer],
  );

  const onChange = React.useCallback(
    ({ target: { name, value } }: { target: { name: string; value: any } }) => {
      const champ = _.replace(name, 'adresse.', '');

      // Changement de type : AdresseForm vide l'adresse (émission éventuellement
      // perdue, cf. ci-dessus).
      appliquer(
        champ === 'type'
          ? { ...toAdresseFormValue(undefined, { articleField }), type: value }
          : { ...courant.current, [champ]: value },
      );
    },
    [appliquer, articleField],
  );

  return { adresseFormValue, onItemChange, onChange };
};

export default useAdresseFormFiche;
